import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  TextField,
  Divider,
  Avatar,
  Badge,
  IconButton,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Menu
} from '@mui/material';
import {
  Work as WorkIcon,
  Chat as ChatIcon,
  Assignment as TaskIcon,
  Storage as StorageIcon,
  Email as EmailIcon,
  Folder as FolderIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  ArrowBack as ArrowBackIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  DataUsage as DataUsageIcon,
  Add as AddIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Cloud as CloudIcon,
  Database as DatabaseIcon,
  Api as ApiIcon,
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { 
  getHiredAgentById, 
  chatWithAgentWorkforce,
  getTasksByAgent,
  createAgentTask,
  updateAgentTask,
  deleteAgentTask,
  executeAgentTask,
  getTaskExecutionHistory,
  getAllEntities,
  getTriggersByEntity
} from '../../api';
import { useGlobalContext } from '../../GlobalProvider';
import AgentChat from './AgentChat';
import AgentTasks from './AgentTasks';
import AgentContext from './AgentContext';
import AgentPerformance from './AgentPerformance';
import AgentOverviewDetails from './AgentOverviewDetails';

const AgentWorkspace = () => {
  const { agentId } = useParams();
  const { appId } = useGlobalContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get the tab from URL params, default to 'chat'
  const selectedTab = searchParams.get('tab') || 'chat';
  
  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Real data from backend
  const [agent, setAgent] = useState(null);
  const [agentTasks, setAgentTasks] = useState([]);
  const [entities, setEntities] = useState([]);
  const [externalSources, setExternalSources] = useState([]);
  const [sharedFolders, setSharedFolders] = useState([]);
  const [emails, setEmails] = useState([]);

  // Fetch agent data
  useEffect(() => {
    if (agentId && appId) {
      fetchAgentData();
      fetchAgentTasks();
      fetchContextData();
    }
  }, [agentId, appId]);

  const fetchAgentData = async () => {
    try {
      setIsLoading(true);
      const response = await getHiredAgentById(agentId, appId);
      setAgent(response.data.data);
    } catch (err) {
      console.error('Error fetching agent:', err);
      setError('Failed to fetch agent data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgentTasks = async () => {
    try {
      const response = await getTasksByAgent(agentId, appId);
      setAgentTasks(response.data.data || []);
    } catch (err) {
      console.error('Error fetching agent tasks:', err);
    }
  };

  const fetchContextData = async () => {
    try {
      // Fetch entities
      const entitiesResponse = await getAllEntities({ appId, limit: 50 });
      setEntities(entitiesResponse.data.data || []);

      // Mock external sources (in real implementation, these would come from backend)
      setExternalSources([
        { id: 1, name: 'Weather API', type: 'API', status: 'Connected', lastSync: '5 minutes ago', endpoint: 'https://api.weather.com' },
        { id: 2, name: 'Traffic Database', type: 'Database', status: 'Connected', lastSync: '2 minutes ago', endpoint: 'mongodb://traffic-db' },
        { id: 3, name: 'Emergency Services', type: 'API', status: 'Connected', lastSync: '1 minute ago', endpoint: 'https://emergency-api.gov' },
        { id: 4, name: 'Public Transport', type: 'API', status: 'Disconnected', lastSync: '1 hour ago', endpoint: 'https://transport-api.city' }
      ]);

      // Mock shared folders (in real implementation, these would come from backend)
      setSharedFolders([
        { id: 1, name: 'Traffic Reports', files: 24, lastModified: '2 hours ago', size: '156 MB' },
        { id: 2, name: 'Safety Data', files: 156, lastModified: '1 hour ago', size: '2.3 GB' },
        { id: 3, name: 'Environmental Monitoring', files: 89, lastModified: '30 minutes ago', size: '890 MB' },
        { id: 4, name: 'Incident Logs', files: 203, lastModified: '15 minutes ago', size: '1.2 GB' }
      ]);

      // Mock emails (in real implementation, these would come from backend)
      setEmails([
        { id: 1, from: 'traffic.manager@city.gov', subject: 'Urgent: Traffic Analysis Needed', unread: true, date: '2 hours ago', priority: 'high' },
        { id: 2, from: 'safety.coordinator@city.gov', subject: 'Monthly Safety Report Due', unread: false, date: '1 day ago', priority: 'medium' },
        { id: 3, from: 'system@smartcity.com', subject: 'System Maintenance Notice', unread: false, date: '2 days ago', priority: 'low' }
      ]);
    } catch (err) {
      console.error('Error fetching context data:', err);
    }
  };

  const handleTaskAction = async (taskId, action) => {
    try {
      switch (action) {
        case 'execute':
          await executeAgentTask(taskId, appId);
          break;
        case 'pause':
          await updateAgentTask(taskId, { status: 'paused' }, appId);
          break;
        case 'resume':
          await updateAgentTask(taskId, { status: 'active' }, appId);
          break;
        case 'delete':
          await deleteAgentTask(taskId, appId);
          break;
        default:
          break;
      }
      fetchAgentTasks(); // Refresh tasks
    } catch (err) {
      console.error(`Error performing task action ${action}:`, err);
      setError(`Failed to ${action} task`);
    }
  };

  const handleCreateTask = async (taskData) => {
    try {
      const fullTaskData = {
        ...taskData,
        agentWorkforceId: agentId,
        appId,
        status: 'pending',
        isActive: true
      };
      
      await createAgentTask(fullTaskData, appId);
      fetchAgentTasks(); // Refresh tasks
      return { success: true };
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task');
      return { success: false, error: err.message };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Busy': return 'warning';
      case 'Available': return 'info';
      case 'Inactive': return 'error';
      case 'Training': return 'secondary';
      case 'Offboarded': return 'error';
      case 'Connected': return 'success';
      case 'Disconnected': return 'error';
      default: return 'default';
    }
  };

  // Handle menu open
  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Handle menu item click
  const handleMenuItemClick = (action) => {
    handleMenuClose();
    
    switch (action) {
      case 'chat':
        navigate(`/agent-workspace/${agentId}?tab=chat`);
        break;
      case 'overview':
        navigate(`/agent-workspace/${agentId}`);
        break;
      case 'tasks':
        navigate(`/agent-workspace/${agentId}?tab=tasks`);
        break;
      case 'context':
        navigate(`/agent-workspace/${agentId}?tab=context`);
        break;
      case 'performance':
        navigate(`/agent-workspace/${agentId}?tab=performance`);
        break;
      case 'configure':
        // Open configuration dialog
        break;
      case 'offboard':
        // Open offboarding dialog
        break;
      default:
        break;
    }
  };

  if (isLoading && !agent) {
    return (
      <Backdrop open={true}>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (!agent) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Agent not found. Please check the URL or go back to the agent list.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/agent-workforce')}>
          <ArrowBackIcon />
        </IconButton>
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          <WorkIcon />
        </Avatar>
        <Box>
          <Typography variant="h4" component="h1">
            {agent.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {agent.category} • {agent.shortDescription}
          </Typography>
          {selectedTab !== 'chat' && (
            <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
              {selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)} View
            </Typography>
          )}
        </Box>
        <Chip 
          label={agent.status} 
          color={getStatusColor(agent.status)}
          sx={{ ml: 2 }}
        />
        <IconButton
          onClick={handleMenuOpen}
          sx={{ ml: 'auto' }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Content Area */}
        {selectedTab === 'chat' ? (
          // Chat view - full width chat interface
          <Grid item xs={12}>
            <AgentChat 
              agent={agent}
              agentId={agentId}
              appId={appId}
            />
          </Grid>
        ) : (
          // Other views - full width content
          <Grid item xs={12}>
            {selectedTab === 'overview' && (
              <AgentOverviewDetails agent={agent} />
            )}

            {selectedTab === 'tasks' && (
              <AgentTasks 
                agent={{...agent, appId}}
                tasks={agentTasks}
                onTaskAction={handleTaskAction}
                onCreateTask={handleCreateTask}
                onRefreshTasks={fetchAgentTasks}
              />
            )}

            {selectedTab === 'context' && (
              <AgentContext 
                agent={agent}
                entities={entities}
                externalSources={externalSources}
                sharedFolders={sharedFolders}
                emails={emails}
                onRefreshData={fetchContextData}
            />
            )}

            {selectedTab === 'performance' && (
              <AgentPerformance agent={agent} />
            )}
          </Grid>
        )}
      </Grid>

      {/* Agent Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 2
          }
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick('chat')}>
          <ListItemIcon>
            <ChatIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Chat with Agent</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleMenuItemClick('overview')}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Agent Overview</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleMenuItemClick('tasks')}>
          <ListItemIcon>
            <TaskIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Tasks</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleMenuItemClick('context')}>
          <ListItemIcon>
            <StorageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Data & Context</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleMenuItemClick('performance')}>
          <ListItemIcon>
            <AnalyticsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Performance Analytics</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => handleMenuItemClick('configure')}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Configure Agent</ListItemText>
        </MenuItem>
        
        {agent.status !== 'Offboarded' && (
          <MenuItem 
            onClick={() => handleMenuItemClick('offboard')}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Offboard Agent</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Container>
  );
};

export default AgentWorkspace; 